// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/commercial/CommercialAdministrationPanel.jsx

import React, { useMemo, useState } from "react";
import "./EntitlementAdministration.css";

const COMMERCIAL_SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "agreement", label: "Agreement" },
  { key: "plan", label: "Plans & Upgrades" },
  { key: "billing", label: "Billing" },
  { key: "payments", label: "Invoices & Payments" },
  { key: "history", label: "Commercial History" },
];

function cleanValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

function formatEnumLabel(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value).replaceAll("_", " ");
}

function formatPricingInterval(value) {
  const normalized = String(value || "").trim().toLowerCase();
  const labels = {
    annual: "Annual",
    monthly: "Monthly",
    fixed_term: "Fixed term",
  };
  return labels[normalized] || formatEnumLabel(value);
}

function formatPricingBasis(value) {
  const normalized = String(value || "").trim().toLowerCase();
  const labels = {
    fixed: "fixed price",
    starting_at: "starting at",
    range: "range",
    custom: "custom",
  };
  return labels[normalized] || formatEnumLabel(value);
}

function formatDate(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return cleanValue(value);
  return parsed.toLocaleDateString();
}

function formatMoneyMinor(value, currency = "USD") {
  if (!Number.isInteger(value)) return "Custom";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(value / 100);
  } catch {
    return `${currency || "USD"} ${value}`;
  }
}

function priceDefinitionLabel(price = {}) {
  if (price.pricing_basis === "range") {
    return `${formatMoneyMinor(price.minimum_amount_minor, price.currency)}–${formatMoneyMinor(
      price.maximum_amount_minor,
      price.currency
    )}`;
  }
  if (price.pricing_basis === "starting_at") {
    return `Starting at ${formatMoneyMinor(price.minimum_amount_minor, price.currency)}`;
  }
  if (price.pricing_basis === "custom") return "Custom";
  return formatMoneyMinor(price.amount_minor, price.currency);
}

function statusClass(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "active") return "enterprise-admin-status enterprise-admin-status-active";
  if (["canceled", "cancelled", "terminated"].includes(normalized)) {
    return "enterprise-admin-status enterprise-admin-status-canceled";
  }
  return "enterprise-admin-status";
}

function MetricCard({ label, value, detail }) {
  return (
    <article className="enterprise-admin-metric-card">
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
      {detail ? <small>{detail}</small> : null}
    </article>
  );
}

function EmptyCommercialState({ title, children }) {
  return (
    <div className="enterprise-admin-empty">
      <strong>{title}</strong>
      <p>{children}</p>
    </div>
  );
}

function OverviewSection({ accountData, pricingData }) {
  const account = accountData?.account || null;
  const agreements = accountData?.agreements || [];
  const activeAgreement =
    agreements.find((agreement) => agreement.status === "active") || agreements[0] || null;
  const pricingLines = pricingData?.agreement_pricing_lines || [];
  const softwareLine =
    pricingLines.find(
      (line) => line.fee_category === "software" && line.status === "active"
    ) ||
    pricingLines.find((line) => line.fee_category === "software") ||
    null;

  return (
    <div className="enterprise-admin-commercial-stack">
      <div className="enterprise-admin-metrics-grid">
        <MetricCard
          label="Commercial setup"
          value={accountData?.configured ? "Configured" : "Not configured"}
          detail="Organization commercial setup"
        />
        <MetricCard
          label="Account status"
          value={account?.status ? cleanValue(account.status) : "—"}
          detail={account?.legal_name || "No commercial account"}
        />
        <MetricCard
          label="Agreement"
          value={activeAgreement?.status ? cleanValue(activeAgreement.status) : "—"}
          detail={activeAgreement?.agreement_reference || "No agreement on record"}
        />
        <MetricCard
          label="Plan"
          value={softwareLine?.package_display_name || "—"}
          detail={
            softwareLine?.agreed_amount_minor !== null &&
            softwareLine?.agreed_amount_minor !== undefined
              ? formatMoneyMinor(softwareLine.agreed_amount_minor, softwareLine.currency)
              : "No plan price recorded"
          }
        />
      </div>

      <div className="enterprise-admin-two-column">
        <article className="enterprise-admin-side-card">
          <p className="enterprise-admin-eyebrow">Commercial authority</p>
          <h3>Contract-driven and provider-neutral</h3>
          <p>
            OASYS owns the commercial agreement, package, and agreed-price semantics.
            Provider references are metadata only and do not define package identity,
            entitlements, or product access.
          </p>
          <dl>
            <div>
              <dt>Legal name</dt>
              <dd>{account?.legal_name || "Not configured"}</dd>
            </div>
            <div>
              <dt>Billing contact</dt>
              <dd>{account?.billing_email || "Not configured"}</dd>
            </div>
            <div>
              <dt>Agreement reference</dt>
              <dd>{activeAgreement?.agreement_reference || "Not configured"}</dd>
            </div>
            <div>
              <dt>Effective date</dt>
              <dd>{formatDate(activeAgreement?.effective_at)}</dd>
            </div>
          </dl>
        </article>

        <article className="enterprise-admin-attention-card">
          <p className="enterprise-admin-eyebrow">Governed administration</p>
          <h3>Commercial administration is currently read-only</h3>
          <p>
            Commercial account creation, agreement changes, provider execution, and
            entitlement enforcement require separate governed workflows and are not
            performed from this read-only surface.
          </p>
          <ul>
            <li>Signed sold price remains separate from current list price.</li>
            <li>Agreement changes must never silently reprice an existing contract.</li>
            <li>Payment state must never directly trigger tenant purge.</li>
          </ul>
        </article>
      </div>
    </div>
  );
}

function AgreementSection({ accountData }) {
  const agreements = accountData?.agreements || [];
  const snapshots = accountData?.contract_snapshots || [];

  if (!accountData?.configured || !agreements.length) {
    return (
      <EmptyCommercialState title="No commercial agreement configured">
        The active price catalog remains visible, but this tenant has no commercial
        agreement or immutable contract snapshot on record.
      </EmptyCommercialState>
    );
  }

  return (
    <div className="enterprise-admin-commercial-stack">
      {agreements.map((agreement) => {
        const agreementSnapshots = snapshots.filter(
          (snapshot) => snapshot.agreement_id === agreement.id
        );
        return (
          <article className="enterprise-admin-commercial-card" key={agreement.id}>
            <div className="enterprise-admin-commercial-card-heading">
              <div>
                <p className="enterprise-admin-eyebrow">Agreement</p>
                <h3>{agreement.agreement_reference || agreement.agreement_type || "Agreement"}</h3>
              </div>
              <span className={statusClass(agreement.status)}>
                {cleanValue(agreement.status, "unknown")}
              </span>
            </div>
            <dl className="enterprise-admin-commercial-definition-grid">
              <div>
                <dt>Type</dt>
                <dd>{cleanValue(agreement.agreement_type)}</dd>
              </div>
              <div>
                <dt>Billing</dt>
                <dd>{cleanValue(agreement.billing_cadence)}</dd>
              </div>
              <div>
                <dt>Payment terms</dt>
                <dd>{cleanValue(agreement.payment_terms)}</dd>
              </div>
              <div>
                <dt>Signed</dt>
                <dd>{formatDate(agreement.signed_at)}</dd>
              </div>
              <div>
                <dt>Effective date</dt>
                <dd>{formatDate(agreement.effective_at)}</dd>
              </div>
              <div>
                <dt>Renewal</dt>
                <dd>{formatDate(agreement.renewal_at)}</dd>
              </div>
              <div>
                <dt>Ends</dt>
                <dd>{formatDate(agreement.ends_at)}</dd>
              </div>
              <div>
                <dt>Contract snapshots</dt>
                <dd>{agreementSnapshots.length}</dd>
              </div>
            </dl>
          </article>
        );
      })}

      {snapshots.length ? (
        <div className="enterprise-admin-table enterprise-admin-commercial-table">
          <div className="enterprise-admin-table-row enterprise-admin-table-head">
            <span>Snapshot</span>
            <span>Version</span>
            <span>Effective</span>
            <span>Captured</span>
          </div>
          {snapshots.map((snapshot) => (
            <div className="enterprise-admin-table-row" key={snapshot.id}>
              <div className="enterprise-admin-table-primary">
                <strong>{snapshot.document_reference || snapshot.snapshot_sha256 || "Contract snapshot"}</strong>
                <small>{snapshot.source || "commercial authority"}</small>
              </div>
              <span>{cleanValue(snapshot.version)}</span>
              <span>{formatDate(snapshot.effective_at)}</span>
              <span>{formatDate(snapshot.captured_at)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function createPlanRequestIdempotencyKey() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `commercial-plan-request:${window.crypto.randomUUID()}`;
  }
  return `commercial-plan-request:${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

function PlanRequestModal({
  request,
  currentPlanName,
  organizationName,
  defaultContactName,
  defaultEmail,
  onClose,
  onSubmit,
}) {
  const [contactName, setContactName] = useState(defaultContactName || "");
  const [workEmail, setWorkEmail] = useState(defaultEmail || "");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [idempotencyKey] = useState(() => createPlanRequestIdempotencyKey());

  if (!request) return null;

  const requestedPlanName = request?.display_name || "OASYS plan";
  const requestMode =
    request?.request_mode ||
    (["enterprise", "design_partner"].includes(
      String(request?.package_type || "").trim().toLowerCase()
    )
      ? "sales"
      : "upgrade");
  const title =
    requestMode === "sales"
      ? `Talk to Sales about ${requestedPlanName}`
      : `Upgrade to ${requestedPlanName}`;
  const hasRequestAuthority = typeof onSubmit === "function";
  const canSubmit =
    hasRequestAuthority &&
    contactName.trim().length >= 2 &&
    workEmail.trim().length > 0 &&
    !submitting;

  const submitRequest = async () => {
    if (!canSubmit && !submission) return;
    if (!hasRequestAuthority || submitting) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      const result = await onSubmit({
        requestedPlan: request,
        contactName: contactName.trim(),
        workEmail: workEmail.trim(),
        message: message.trim(),
        idempotencyKey,
      });
      setSubmission(result || {});
    } catch (error) {
      setSubmitError(
        String(error?.message || "Unable to submit the commercial plan request.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitRequest();
  };

  const deliveryComplete = submission?.delivery_complete === true;
  const submittedRequest = submission?.request || null;

  return (
    <div
      className="enterprise-admin-plan-request-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <section
        className="enterprise-admin-plan-request-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="enterprise-admin-plan-request-title"
      >
        <div className="enterprise-admin-plan-request-header">
          <div>
            <p className="enterprise-admin-eyebrow">Plan request</p>
            <h3 id="enterprise-admin-plan-request-title">{title}</h3>
            <p>
              Send OASYS Sales your request. This starts a commercial review and does not
              directly change product access, billing state, or entitlement authority.
            </p>
          </div>
          <button
            type="button"
            className="enterprise-admin-plan-request-close"
            onClick={onClose}
            aria-label="Close plan request"
            disabled={submitting}
          >
            ×
          </button>
        </div>

        <div className="enterprise-admin-plan-request-summary">
          <div>
            <span>Organization</span>
            <strong>{organizationName || "Current organization"}</strong>
          </div>
          <div>
            <span>Current plan</span>
            <strong>{currentPlanName || "Not assigned"}</strong>
          </div>
          <div>
            <span>Requested plan</span>
            <strong>{requestedPlanName}</strong>
          </div>
        </div>

        {submission ? (
          <div
            className={`enterprise-admin-plan-request-result${
              deliveryComplete ? " is-complete" : " is-pending"
            }`}
            role="status"
            aria-live="polite"
          >
            <p className="enterprise-admin-eyebrow">
              {deliveryComplete ? "Request received" : "Request recorded"}
            </p>
            <h4>
              {deliveryComplete
                ? "OASYS Sales has been notified."
                : "Your request is safely recorded."}
            </h4>
            <p>
              {deliveryComplete
                ? `We sent a confirmation to ${submittedRequest?.work_email || workEmail}. OASYS Sales will review the request and follow up.`
                : "The commercial request is durable, but one or more email notifications are still pending. You can retry delivery without creating a duplicate request."}
            </p>
            {submittedRequest?.id ? (
              <small>Request ID: {submittedRequest.id}</small>
            ) : null}
            <p className="enterprise-admin-plan-request-boundary-note">
              This request did not directly change your plan, billing state, product
              access, or entitlement authority.
            </p>
            <div className="enterprise-admin-plan-request-actions">
              {!deliveryComplete ? (
                <button
                  type="button"
                  className="enterprise-admin-plan-primary-button"
                  onClick={submitRequest}
                  disabled={submitting}
                >
                  {submitting ? "Retrying…" : "Retry notification delivery"}
                </button>
              ) : null}
              <button
                type="button"
                className="enterprise-admin-plan-secondary-button"
                onClick={onClose}
                disabled={submitting}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className="enterprise-admin-plan-request-form" onSubmit={handleSubmit}>
            <label>
              <span>Contact name</span>
              <input
                type="text"
                value={contactName}
                onChange={(event) => setContactName(event.target.value)}
                placeholder="Your name"
                autoComplete="name"
                minLength={2}
                maxLength={160}
                required
                disabled={submitting}
              />
            </label>

            <label>
              <span>Work email</span>
              <input
                type="email"
                value={workEmail}
                onChange={(event) => setWorkEmail(event.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                maxLength={320}
                required
                disabled={submitting}
              />
            </label>

            <label className="enterprise-admin-plan-request-message-field">
              <span>Message <small>Optional</small></span>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Anything OASYS Sales should know about timing, scope, procurement, or your upgrade goals?"
                rows={4}
                maxLength={2000}
                disabled={submitting}
              />
            </label>

            {!hasRequestAuthority ? (
              <div className="enterprise-admin-plan-request-pending-note" role="note">
                <strong>Plan request permission required.</strong>
                <span>
                  Your current role can review commercial information but cannot submit
                  a governed plan request.
                </span>
              </div>
            ) : null}

            {submitError ? (
              <div className="enterprise-admin-plan-request-error" role="alert">
                <strong>Request could not be submitted.</strong>
                <span>{submitError}</span>
              </div>
            ) : null}

            <div className="enterprise-admin-plan-request-actions">
              <button
                type="button"
                className="enterprise-admin-plan-secondary-button"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="enterprise-admin-plan-primary-button"
                disabled={!canSubmit}
                title={
                  !hasRequestAuthority
                    ? "Your role does not have commercial request authority."
                    : undefined
                }
              >
                {submitting
                  ? "Submitting…"
                  : requestMode === "sales"
                    ? "Send request to Sales"
                    : "Submit upgrade request"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

function PlanSection({
  accountData,
  pricingData,
  onUpgradeRequest,
  organizationName,
  requestContactName,
  requestContactEmail,
}) {
  const [plansExpanded, setPlansExpanded] = useState(false);
  const [planRequest, setPlanRequest] = useState(null);
  const packages = pricingData?.catalog?.package_versions || [];
  const prices = pricingData?.catalog?.price_definitions || [];
  const pricingLines = pricingData?.agreement_pricing_lines || [];
  const agreements = accountData?.agreements || [];
  const account = accountData?.account || null;
  const activeAgreement =
    agreements.find((agreement) => agreement.status === "active") || agreements[0] || null;
  const currentSoftwareLine =
    pricingLines.find(
      (line) => line.fee_category === "software" && line.status === "active"
    ) || null;
  const currentPlanName =
    currentSoftwareLine?.package_display_name || currentSoftwareLine?.description || null;
  const currentPlanKey = String(currentSoftwareLine?.package_key || "").trim().toLowerCase();
  const standardPlanRank = {
    foundation: 1,
    assurance: 2,
  };

  const requestModeForPlan = (pkg) => {
    const packageType = String(pkg?.package_type || "").trim().toLowerCase();
    const packageKey = String(pkg?.package_key || "").trim().toLowerCase();
    if (["enterprise", "design_partner"].includes(packageType)) return "sales";

    if (currentPlanKey) {
      const currentRank = standardPlanRank[currentPlanKey];
      const requestedRank = standardPlanRank[packageKey];
      if (
        !currentRank ||
        !requestedRank ||
        requestedRank <= currentRank
      ) {
        return "sales";
      }
    }

    return "upgrade";
  };

  const requestCtaLabel = (pkg) =>
    requestModeForPlan(pkg) === "sales"
      ? "Talk to Sales"
      : `Upgrade to ${pkg?.display_name || "this plan"}`;

  return (
    <div className="enterprise-admin-commercial-stack enterprise-admin-plans-upgrades">
      <section className="enterprise-admin-current-plan-section">
        <div className="enterprise-admin-commercial-subheading">
          <div>
            <p className="enterprise-admin-eyebrow">Your current plan</p>
            <h3>{currentPlanName || "No plan assigned"}</h3>
          </div>
          {currentPlanName ? (
            <span className="enterprise-admin-status enterprise-admin-status-active">Current</span>
          ) : null}
        </div>

        {currentSoftwareLine ? (
          <article className="enterprise-admin-commercial-card enterprise-admin-current-plan-card">
            <div className="enterprise-admin-commercial-card-heading">
              <div>
                <p className="enterprise-admin-eyebrow">Current subscription</p>
                <h3>{currentPlanName}</h3>
              </div>
            </div>
            <p>This is the plan currently recorded for your organization.</p>
            <dl className="enterprise-admin-commercial-definition-grid">
              <div>
                <dt>Price</dt>
                <dd>
                  {formatMoneyMinor(
                    currentSoftwareLine.agreed_amount_minor,
                    currentSoftwareLine.currency
                  )}
                </dd>
              </div>
              <div>
                <dt>Billing cadence</dt>
                <dd>{formatPricingInterval(currentSoftwareLine.pricing_interval)}</dd>
              </div>
              <div>
                <dt>Agreement status</dt>
                <dd>{cleanValue(activeAgreement?.status, "Not recorded")}</dd>
              </div>
              <div>
                <dt>Effective</dt>
                <dd>{formatDate(activeAgreement?.effective_at)}</dd>
              </div>
            </dl>
          </article>
        ) : (
          <EmptyCommercialState title="No plan assigned">
            No active commercial plan is recorded for your organization yet.
          </EmptyCommercialState>
        )}
      </section>

      <section className="enterprise-admin-plan-options-section">
        <button
          type="button"
          className="enterprise-admin-plan-disclosure-button"
          aria-expanded={plansExpanded}
          onClick={() => setPlansExpanded((open) => !open)}
        >
          <span className="enterprise-admin-plan-disclosure-copy">
            <span className="enterprise-admin-eyebrow">Available plans</span>
            <strong>Compare plans</strong>
            <small>Review current OASYS plan options and list pricing.</small>
          </span>
          <span className="enterprise-admin-plan-disclosure-meta">
            <span>{packages.length} plans</span>
            <span className="enterprise-admin-plan-disclosure-chevron" aria-hidden="true">
              {plansExpanded ? "⌃" : "⌄"}
            </span>
          </span>
        </button>

        {plansExpanded ? (
          <div className="enterprise-admin-commercial-catalog-grid enterprise-admin-plan-card-grid">
            {packages.map((pkg) => {
              const packagePrices = prices.filter(
                (price) => price.package_version_id === pkg.id
              );
              const isCurrent =
                Boolean(currentPlanName) &&
                String(pkg.display_name || "").trim().toLowerCase() ===
                  String(currentPlanName).trim().toLowerCase();

              return (
                <article
                  className={`enterprise-admin-commercial-card enterprise-admin-plan-card${
                    isCurrent ? " is-current" : ""
                  }`}
                  key={pkg.id}
                >
                  <div className="enterprise-admin-commercial-card-heading">
                    <div>
                      <p className="enterprise-admin-eyebrow">
                        {formatEnumLabel(pkg.package_type)}
                      </p>
                      <h3>{pkg.display_name}</h3>
                    </div>
                    {isCurrent ? (
                      <span className="enterprise-admin-status enterprise-admin-status-active">
                        Current plan
                      </span>
                    ) : pkg.status && pkg.status !== "active" ? (
                      <span className="enterprise-admin-commercial-catalog-status">
                        {formatEnumLabel(pkg.status)}
                      </span>
                    ) : null}
                  </div>

                  <p className="enterprise-admin-plan-positioning">
                    {pkg.positioning || "OASYS commercial plan"}
                  </p>

                  <div className="enterprise-admin-commercial-price-list">
                    {packagePrices.length ? (
                      packagePrices.map((price) => (
                        <div key={price.id}>
                          <strong>{priceDefinitionLabel(price)}</strong>
                          <span>
                            {formatPricingInterval(price.pricing_interval)} ·{" "}
                            {formatPricingBasis(price.pricing_basis)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div>
                        <strong>Custom pricing</strong>
                        <span>Contact OASYS for plan details</span>
                      </div>
                    )}
                  </div>

                  <div className="enterprise-admin-plan-card-footer">
                    {isCurrent ? (
                      <span className="enterprise-admin-plan-current-label">Your current plan</span>
                    ) : (
                      <button
                        type="button"
                        className="enterprise-admin-plan-upgrade-button"
                        onClick={() => setPlanRequest({ ...pkg, request_mode: requestModeForPlan(pkg) })}
                      >
                        {requestCtaLabel(pkg)}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </section>

      <PlanRequestModal
        key={planRequest?.id || "closed-plan-request"}
        request={planRequest}
        currentPlanName={currentPlanName}
        organizationName={organizationName || account?.legal_name}
        defaultContactName={requestContactName}
        defaultEmail={requestContactEmail || account?.billing_email}
        onClose={() => setPlanRequest(null)}
        onSubmit={onUpgradeRequest}
      />
    </div>
  );
}

function BillingSection({ accountData }) {
  const references = accountData?.provider_references || [];

  return (
    <div className="enterprise-admin-commercial-stack">
      <article className="enterprise-admin-attention-card">
        <p className="enterprise-admin-eyebrow">Billing boundary</p>
        <h3>Billing activity is managed separately</h3>
        <p>
          Provider references may be inspected as metadata, but they do not define
          OASYS package identity, commercial status, entitlements, or product access.
          Billing execution and provider reconciliation are outside this read-only
          commercial authority.
        </p>
      </article>

      {references.length ? (
        <div className="enterprise-admin-table enterprise-admin-commercial-table">
          <div className="enterprise-admin-table-row enterprise-admin-table-head">
            <span>Provider reference</span>
            <span>Provider</span>
            <span>Type</span>
            <span>Status</span>
          </div>
          {references.map((reference) => (
            <div className="enterprise-admin-table-row" key={reference.id}>
              <div className="enterprise-admin-table-primary">
                <strong>{reference.external_reference}</strong>
                <small>{reference.is_primary ? "Primary reference" : "Reference metadata"}</small>
              </div>
              <span>{cleanValue(reference.provider)}</span>
              <span>{cleanValue(reference.reference_type)}</span>
              <span className={statusClass(reference.status)}>{cleanValue(reference.status)}</span>
            </div>
          ))}
        </div>
      ) : (
        <EmptyCommercialState title="No provider references">
          No billing-provider references are linked to this tenant. This does not
          affect the OASYS commercial package or agreement authority.
        </EmptyCommercialState>
      )}
    </div>
  );
}

function PaymentsSection() {
  return (
    <div className="enterprise-admin-commercial-stack">
      <article className="enterprise-admin-attention-card">
        <p className="enterprise-admin-eyebrow">Invoices & payments</p>
        <h3>Payment-provider data is not commercial authority</h3>
        <p>
          OASYS does not create invoices, collect payments, process billing webhooks,
          calculate past-due state, or reconcile provider transactions from this
          commercial administration surface. Provider-generated financial documents
          remain provider authority.
        </p>
      </article>
      <EmptyCommercialState title="Financial transaction data not configured">
        Invoice and payment information will appear here when billing and
        payment-provider integrations are configured.
      </EmptyCommercialState>
    </div>
  );
}

function HistorySection({ historyData }) {
  const items = historyData?.items || [];

  if (!items.length) {
    return (
      <EmptyCommercialState title="No commercial history">
        No append-only commercial events are recorded for this tenant.
      </EmptyCommercialState>
    );
  }

  return (
    <div className="enterprise-admin-audit-list">
      {items.map((item) => (
        <article className="enterprise-admin-audit-event" key={item.id}>
          <div>
            <strong>{cleanValue(item.event_type, "Commercial event")}</strong>
            <span>
              {cleanValue(item.event_category, "commercial")} · {cleanValue(item.source, "OASYS")}
            </span>
          </div>
          <time>{formatDate(item.occurred_at)}</time>
        </article>
      ))}
    </div>
  );
}

export default function CommercialAdministrationPanel({
  accountData,
  pricingData,
  historyData,
  loading,
  error,
  onRetry,
  onUpgradeRequest,
  organizationName,
  requestContactName,
  requestContactEmail,
}) {
  const [activeCommercialSection, setActiveCommercialSection] = useState("overview");

  const tenantSummary = useMemo(() => {
    const agreements = accountData?.agreements || [];
    const pricingLines = pricingData?.agreement_pricing_lines || [];
    const currentAgreement =
      agreements.find((agreement) => agreement.status === "active") || agreements[0] || null;
    const currentPlan =
      pricingLines.find(
        (line) => line.fee_category === "software" && line.status === "active"
      ) || null;

    return {
      plan: currentPlan?.package_display_name || currentPlan?.description || "Not assigned",
      agreement: currentAgreement?.status
        ? formatEnumLabel(currentAgreement.status)
        : "None",
    };
  }, [accountData, pricingData]);

  if (loading) {
    return <div className="enterprise-admin-loading">Loading commercial authority…</div>;
  }

  if (error) {
    return (
      <div className="enterprise-admin-error">
        <h3>Commercial authority unavailable</h3>
        <p>{error}</p>
        {onRetry ? <button type="button" onClick={onRetry}>Retry</button> : null}
      </div>
    );
  }

  const renderCommercialSection = () => {
    if (activeCommercialSection === "agreement") {
      return <AgreementSection accountData={accountData} />;
    }
    if (activeCommercialSection === "plan") {
      return (
        <PlanSection
          accountData={accountData}
          pricingData={pricingData}
          onUpgradeRequest={onUpgradeRequest}
          organizationName={organizationName}
          requestContactName={requestContactName}
          requestContactEmail={requestContactEmail}
        />
      );
    }
    if (activeCommercialSection === "billing") {
      return <BillingSection accountData={accountData} />;
    }
    if (activeCommercialSection === "payments") {
      return <PaymentsSection />;
    }
    if (activeCommercialSection === "history") {
      return <HistorySection historyData={historyData} />;
    }
    return <OverviewSection accountData={accountData} pricingData={pricingData} />;
  };

  return (
    <section className="enterprise-admin-section enterprise-admin-commercial-panel">
      <div className="enterprise-admin-section-header">
        <p className="enterprise-admin-eyebrow">Commercial administration</p>
        <h2>Commercial plan &amp; agreement</h2>
        <p>
          Review your organization&apos;s agreement, current plan, available plan options,
          billing references, and commercial history.
        </p>
      </div>

      <div className="enterprise-admin-commercial-summary" aria-label="Commercial summary">
        <span>Plan: {tenantSummary.plan}</span>
        <span>Agreement: {tenantSummary.agreement}</span>
      </div>

      <nav className="enterprise-admin-commercial-tabs" aria-label="Commercial administration views">
        {COMMERCIAL_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            className={activeCommercialSection === section.key ? "active" : ""}
            onClick={() => setActiveCommercialSection(section.key)}
            aria-current={activeCommercialSection === section.key ? "page" : undefined}
          >
            {section.label}
          </button>
        ))}
      </nav>

      <div className="enterprise-admin-commercial-workspace">
        {renderCommercialSection()}
      </div>
    </section>
  );
}
